import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import EmptyState from "@/components/base/EmptyState";
import { SITE } from "@/config/site";

interface FaqItem {
  id: string;
  group: string;
  q: string;
  a: string;
}

const faqGroups = [
  "Getting Started",
  "For Customers",
  "For Business Owners",
  "Plans & Payments",
  "Account & Support",
];

const faqs: FaqItem[] = [
  { id: "g1", group: "Getting Started", q: "What is Mysore Yellow Pages?", a: "It is a hyperlocal business directory for the Mysore region — Mysore, Mandya, Chamarajanagar and Kodagu (Coorg). We help residents find trusted local businesses and help owners get discovered by nearby customers." },
  { id: "g2", group: "Getting Started", q: "Which areas do you cover?", a: "We currently cover four districts: Mysore, Mandya, Chamarajanagar and Kodagu (Coorg), with locality-level pages inside each district. We're expanding across Karnataka." },
  { id: "g3", group: "Getting Started", q: "Is it free to use the directory?", a: "Yes. Searching, browsing categories, viewing business profiles and contacting businesses is completely free for customers, no account required." },
  { id: "c1", group: "For Customers", q: "How do I find a business near me?", a: "Use the search on the homepage or the directory, and combine a keyword with a category and a location. You can further filter by minimum rating and 'open now only' to narrow the shortlist." },
  { id: "c2", group: "For Customers", q: "Can I trust the ratings and reviews?", a: "Ratings come from customers who engage with listings. Premium and Elite listings also carry a verified badge, meaning we have manually confirmed the business address and owner details." },
  { id: "c3", group: "For Customers", q: "Are the phone numbers and addresses accurate?", a: "We ask owners to keep their details up to date and we review reported issues. If you spot something wrong on a listing, use the 'Report an issue' link on the profile and we'll follow up." },
  { id: "c4", group: "For Customers", q: "How do I find emergency services quickly?", a: "Visit our Emergency Services page for hospitals, ambulances, police, fire and other critical contacts across the region, organised for fast access." },
  { id: "o1", group: "For Business Owners", q: "Is listing my business free?", a: "Yes. Any local business in the Mysore region can create a free listing. You can upgrade to a paid plan later for verified badges and featured placement." },
  { id: "o2", group: "For Business Owners", q: "How do I add my business?", a: "Use the 'List Your Business' page to submit your details. Our team reviews the submission and typically sets up your listing within one working day." },
  { id: "o3", group: "For Business Owners", q: "How do I get a verified badge?", a: "Choose the Premium plan or above and submit your business documents. Our team verifies your address and owner details before granting the badge." },
  { id: "o4", group: "For Business Owners", q: "Can I edit my listing after publishing?", a: "Absolutely. Log in to the owner portal and update your details, photos, opening hours and offers at any time from the dashboard." },
  { id: "o5", group: "For Business Owners", q: "How do I respond to a review?", a: "From your owner dashboard, open the Reviews section to read and reply to customer feedback. Genuine responses help build trust with future customers." },
  { id: "p1", group: "Plans & Payments", q: "What plans are available?", a: "We offer a Free listing plus paid tiers such as Premium and Elite. Paid plans add verification, priority placement, richer media and analytics. See the Listing Plans page for details." },
  { id: "p2", group: "Plans & Payments", q: "How is billing handled?", a: "Plans are billed for the period you choose. Invoices are available from your owner account, and our team can help with any billing questions via the Contact page." },
  { id: "p3", group: "Plans & Payments", q: "Can I cancel or change my plan?", a: "Yes. You can change or cancel your plan from your account. Changes take effect at the start of the next billing period, unless you ask our team to adjust sooner." },
  { id: "a1", group: "Account & Support", q: "Do I need an account to search?", a: "No. Searching and viewing listings does not require an account. An owner account is only needed to manage your own business listing." },
  { id: "a2", group: "Account & Support", q: "How do I reset my password?", a: "Use the 'Forgot password' option on the login page and follow the emailed instructions. If you don't receive it, contact us and we'll help." },
  { id: "a3", group: "Account & Support", q: "How do I contact the team?", a: "You can reach us through the Contact page, email us at " + SITE.email + ", or call " + SITE.phone + ". We're a Mysore-based team and happy to help." },
];

const headerImage =
  "https://readdy.ai/api/search-image?query=Soft%20abstract%20illustration%20of%20a%20friendly%20customer%20support%20scene%20with%20speech%20bubbles%20and%20question%20marks%20in%20warm%20amber%20and%20olive%20tones%20over%20a%20dark%20gradient%2C%20minimal%20textured%20editorial%20style%20with%20strong%20contrast&width=1600&height=600&seq=myyp-faq-01&orientation=landscape";

export default function Faqs() {
  const [query, setQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState<string>("All");
  const [open, setOpen] = useState<string | null>(faqs[0]?.id ?? null);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return faqs.filter((f) => {
      const matchesGroup = activeGroup === "All" || f.group === activeGroup;
      const matchesQuery = !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
      return matchesGroup && matchesQuery;
    });
  }, [query, activeGroup]);

  const grouped = useMemo(() => {
    const map = new Map<string, FaqItem[]>();
    filtered.forEach((f) => {
      if (!map.has(f.group)) map.set(f.group, []);
      map.get(f.group)!.push(f);
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <>
      <Seo
        title="Frequently Asked Questions"
        description="Answers to common questions about searching the Mysore Yellow Pages directory, listing your business, verified badges, plans and account support."
        keywords="Mysore Yellow Pages FAQ, business listing questions, directory help"
        path="/faqs"
      />

      <div className="relative w-full">
        <div className="absolute inset-0">
          <img src={headerImage} alt="Frequently asked questions" title="Mysore Yellow Pages FAQs" className="h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/80 via-foreground-950/65 to-foreground-950/85"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
          <div className="[&_a]:text-background-200 [&_span]:text-background-200">
            <Breadcrumbs items={[{ label: "FAQs" }]} />
          </div>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl font-extrabold tracking-tight text-background-50">
            Frequently asked questions
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-lg text-background-200 leading-relaxed">
            Everything you might want to know about searching, listing and managing your presence on {SITE.name}.
          </p>
          <div className="mt-6 max-w-xl">
            <div className="relative">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions…"
                className="w-full rounded-md border border-background-200 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-foreground-500">Browse by topic</h2>
              <div className="mt-3 flex flex-wrap gap-2 lg:flex-col lg:gap-1">
                {["All", ...faqGroups].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setActiveGroup(g)}
                    className={`rounded-md px-3 py-2 text-left text-sm font-medium transition-colors cursor-pointer ${
                      activeGroup === g ? "bg-primary-100 text-primary-800" : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-4">
              <h3 className="inline-flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-customer-service-2-line text-primary-700"></i> Still stuck?
              </h3>
              <p className="mt-2 text-sm text-foreground-700">Our local team is happy to help you directly.</p>
              <Link
                to="/contact"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-800 hover:text-primary-900 cursor-pointer"
              >
                Contact us <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>

          <div>
            {filtered.length === 0 ? (
              <EmptyState
                icon="ri-question-line"
                title="No matching questions"
                description="Try a different keyword or browse another topic. You can also just ask us directly."
                action={{ label: "Contact us", to: "/contact" }}
              />
            ) : (
              <div className="space-y-8">
                {grouped.map(([group, items]) => (
                  <div key={group}>
                    <h2 className="font-heading text-lg md:text-xl font-extrabold text-foreground-950">{group}</h2>
                    <div className="mt-3 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                      {items.map((f) => (
                        <div key={f.id}>
                          <button
                            type="button"
                            onClick={() => setOpen(open === f.id ? null : f.id)}
                            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                          >
                            <span className="font-heading text-sm font-bold text-foreground-950">{f.q}</span>
                            <i className={`ri-add-line text-lg text-foreground-500 transition-transform ${open === f.id ? "rotate-45" : ""}`}></i>
                          </button>
                          {open === f.id && (
                            <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{f.a}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}